import React, { useState, useEffect } from "react";
import { formatCurrency, getAlert } from "../../../utils/SamuiUtils";
import {
  formatThaiDateUi,
  formatThaiDate,
} from "../../../utils/DateUtils";
import Datetime from "react-datetime";
import moment from "moment";

const InvoiceModal = ({
  showInvModal,
  handleInvClose,
  onRowSelectInv,
  invDataList,
  arId,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredInvDataList, setFilteredInvDataList] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [docStatusPaid, setDocStatusPaid] = useState(0);

  useEffect(() => {
    if (!invDataList || !Array.isArray(invDataList)) {
      setFilteredInvDataList([]);
      return;
    }

    setFilteredInvDataList(
      invDataList.filter((inv) => {
        const isSearchMatch =
          inv.Doc_No.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (inv.AR_Name ? inv.AR_Name.toLowerCase().includes(searchTerm.toLowerCase()) : false);

        // ถ้ามีการส่ง arId มา ให้กรองตาม arId แต่ถ้าไม่ส่งมา (เช่น Receipt Voucher) ให้แสดงทั้งหมด

        const isDateInRange =
          !startDate ||
          !endDate ||
          moment(formatThaiDate(inv.Doc_Date)).isBetween(
            startDate,
            endDate,
            "day",
            "[]"
          );

        // Filter by Doc_Status_Paid (0 = All, 1 = Waiting, 2 = Outstanding, 3 = Paid)
        const isStatusMatch = docStatusPaid === 0 || inv.Doc_Status_Paid === docStatusPaid;

        return isSearchMatch && isDateInRange && isStatusMatch;
      })
    );
  }, [searchTerm, invDataList, arId, startDate, endDate, docStatusPaid]);

  useEffect(() => {
    if (showInvModal && invDataList && invDataList.length > 0) {
      setSearchTerm("");
      setSelectedItems([]);
      const dates = invDataList.map(
        (inv) => new Date(formatThaiDate(inv.Doc_Date || new Date()))
      );
      const minDate = moment(Math.min(...dates));
      const maxDate = moment(Math.max(...dates));
      setStartDate(minDate);
      setEndDate(maxDate);
      setDocStatusPaid(0); // Default to Waiting
    }
  }, [showInvModal, invDataList]);

  const checkArNameConsistency = (items) => {
    if (items.length > 0) {
      const arName = items[0].AR_Name;
      const isConsistent = items.every((item) => item.AR_Name === arName);
      if (!isConsistent) {
        getAlert("FAILED", "กรุณาเลือกลูกค้าเป็นรายเดียวกัน");
      }
      return isConsistent;
    }
    return true;
  };

  const handleCheckboxChange = (inv) => {
    const updatedSelectedItems = selectedItems.some(
      (item) => item.Doc_No === inv.Doc_No
    )
      ? selectedItems.filter((item) => item.Doc_No !== inv.Doc_No)
      : [...selectedItems, inv];

    setSelectedItems(updatedSelectedItems);
  };

  const handleConfirmSelection = () => {
    if (checkArNameConsistency(selectedItems)) {
      onRowSelectInv(selectedItems);
    }
  };

  const clearSelection = () => {
    setSelectedItems([]);
  };

  return (
    <div
      className={`modal ${showInvModal ? "show" : ""}`}
      style={{ display: showInvModal ? "block" : "none" }}
      tabIndex="-1"
      role="dialog"
    >
      <div className="modal-dialog modal-xl" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">รายชื่อใบกำกับภาษี (INV)</h5>
            <button type="button" className="close" onClick={handleInvClose}>
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <div className="row">
              <div className="col-4">
                <div className="form-group">
                  <span className="fw-bold">ค้นหาเอกสาร</span>
                  <input
                    style={{ width: "100%" }}
                    type="text"
                    className="form-control"
                    placeholder="ค้นหาเลขที่เอกสาร หรือ ชื่อลูกค้า"
                    value={searchTerm || ""}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-4">
                <div className="form-group">
                  <span className="fw-bold">สถานะจ่าย</span>
                  <div className="radio-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="docStatusPaid"
                      value={0}
                      id="status0"
                      checked={docStatusPaid === 0}
                      onChange={(e) =>
                        clearSelection() + setDocStatusPaid(parseInt(e.target.value))
                      }
                    />
                    <label className="form-check-label ml-2" htmlFor="status0">
                      ทั้งหมด
                    </label>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="docStatusPaid"
                      value={1}
                      id="status1"
                      checked={docStatusPaid === 1}
                      onChange={(e) =>
                        clearSelection() + setDocStatusPaid(parseInt(e.target.value))
                      }
                    />
                    <label className="form-check-label ml-2" htmlFor="status1">
                      รอจ่าย
                    </label>
                    <input
                      className="form-check-input"
                      type="radio"
                      name="docStatusPaid"
                      value={2}
                      id="status2"
                      checked={docStatusPaid === 2}
                      onChange={(e) =>
                        clearSelection() + setDocStatusPaid(parseInt(e.target.value))
                      }
                    />
                    <label className="form-check-label ml-2" htmlFor="status2">
                      ค้างจ่าย
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-2">
                <div className="form-group">
                  <span className="fw-bold">วันที่เริ่มต้น</span>
                  <Datetime
                    className="input-spacing-input-date"
                    value={startDate}
                    dateFormat="DD-MM-YYYY"
                    timeFormat={false}
                    onChange={(date) => setStartDate(date)}
                    inputProps={{ readOnly: true, disabled: false }}
                  />
                </div>
              </div>
              <div className="col-2">
                <div className="form-group">
                  <span className="fw-bold">วันที่สิ้นสุด</span>
                  <Datetime
                    className="input-spacing-input-date"
                    value={endDate}
                    dateFormat="DD-MM-YYYY"
                    timeFormat={false}
                    onChange={(date) => setEndDate(date)}
                    inputProps={{ readOnly: true, disabled: false }}
                  />
                </div>
              </div>
            </div>

            <div
              className="table-responsive"
              style={{ maxHeight: "600px", overflowY: "auto" }}
            >
              <table className="table table-striped table-hover">
                <thead className="thead-dark">
                  <tr>
                    <th className="text-center" style={{ width: "1%" }}>
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedItems(
                            checked ? filteredInvDataList : []
                          );
                        }}
                        checked={
                          filteredInvDataList.length > 0 &&
                          selectedItems.length === filteredInvDataList.length
                        }
                      />
                    </th>
                    <th className="text-center" style={{ width: "15%" }}>
                      เลขที่เอกสาร (INV)
                    </th>
                    <th className="text-center" style={{ width: "30%" }}>
                      ชื่อลูกค้า
                    </th>
                    <th className="text-center" style={{ width: "25%" }}>
                      รายละเอียดเอกสาร
                    </th>
                    <th className="text-center" style={{ width: "10%" }}>
                      วันที่รับเข้า
                    </th>
                    <th className="text-center" style={{ width: "7%" }}>
                      ราคารวม
                    </th>
                    <th className="text-center" style={{ width: "12%" }}>
                      สถานะจ่าย
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvDataList.length > 0 ? (
                    filteredInvDataList.map((inv, index) => (
                      <tr
                        key={inv.Doc_ID || index + 1}
                        onClick={() => handleCheckboxChange(inv)}
                        style={{
                          cursor: "pointer",
                          backgroundColor: selectedItems.some(
                            (item) => item.Doc_No === inv.Doc_No
                          )
                            ? "rgb(105, 188, 216)"
                            : "transparent",
                          transition: "background-color 0.3s ease",
                        }}
                      >
                        <td className="text-center">
                          <input
                            type="checkbox"
                            checked={selectedItems.some(
                              (item) => item.Doc_No === inv.Doc_No
                            )}
                            onChange={() => handleCheckboxChange(inv)}
                            style={{ cursor: "pointer" }}
                          />
                        </td>
                        <td className="text-center">{inv.Doc_No}</td>
                        <td className="text-left">{inv.AR_Name}</td>
                        <td className="text-left">{inv.Doc_Remark1}</td>
                        <td className="text-center">
                          {formatThaiDateUi(inv.Doc_Date)}
                        </td>
                        <td className="text-end">
                          {formatCurrency(inv.Pay_Total)}
                        </td>
                        <td className="text-center">
                          {inv.DocStatusPaid_Name}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        <h5>ไม่พบข้อมูล</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="modal-footer">
            <button
              className="btn btn-primary"
              onClick={handleConfirmSelection}
            >
              ยืนยันการเลือก
            </button>
            <button className="btn btn-danger" onClick={clearSelection}>
              ยกเลิกการเลือก
            </button>
            <button className="btn btn-secondary" onClick={handleInvClose}>
              ปิด
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;
